"use client";

import { ArrowDown, Target, NotebookPen, ListChecks } from "lucide-react";
import { useTranslation } from "@/lib/i18n/context";

/** Renders an input string, colouring a leading /slash-command in terracotta. */
function CommandInput({ input }: { input: string }) {
  const isSlash = input.startsWith("/");
  const firstSpace = input.indexOf(" ");
  const cmd = isSlash && firstSpace > 0 ? input.slice(0, firstSpace) : "";
  const rest = isSlash && firstSpace > 0 ? input.slice(firstSpace) : input;
  return (
    <span className="font-mono text-[12.5px] leading-relaxed text-[var(--ink)] break-words [overflow-wrap:anywhere]">
      {cmd && <span className="text-[var(--terracotta)]">{cmd}</span>}
      {rest}
    </span>
  );
}

/** A labelled input row (the two ways to capture a task). */
function InputRow({ label, input }: { label: string; input: string }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">{label}</div>
      <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-3.5 py-2.5">
        <CommandInput input={input} />
      </div>
    </div>
  );
}

/** The in-app confirmation chip (mirrors SideChat's CommandButton look). */
function ConfirmChip({ verb, detail }: { verb: string; detail: string }) {
  return (
    <span className="inline-flex max-w-full items-start gap-1.5 rounded-lg border border-[var(--terracotta)]/35 bg-[var(--terracotta)]/10 px-2.5 py-1.5 text-left align-middle text-[12.5px] text-[var(--ink-soft)] transition hover:border-[var(--terracotta)] hover:bg-[var(--terracotta)]/15">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="mt-[3px] shrink-0 text-[var(--terracotta)]">
        <path d="M3 2.5l5 3.5-5 3.5z" fill="currentColor" />
      </svg>
      <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">
        <span className="font-medium text-[var(--terracotta)]">{verb}</span>
        <span className="text-[var(--ink-soft)]"> · {detail}</span>
      </span>
    </span>
  );
}

export function AiAssistantDeepDive() {
  const { t, lang } = useTranslation();
  const en = lang === "en";
  const p = t.landing.cover.aiAssistantPage;
  const assistantName = lang === "en" ? "AI Assistant" : "AI pomočnik";
  const youLabel = t.landing.cover.assistant.demoYou;

  const pillIcons = [Target, NotebookPen, ListChecks];

  return (
    <>
      {/* ── Section 1 · Natural language + slash commands ───────────── */}
      <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-[100px] z-[2]">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
          {/* Copy */}
          <div>
            <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {p.commands.kicker}
            </div>
            <h2
              className="font-display text-[clamp(30px,3.6vw,48px)] leading-[1.06] tracking-[-0.02em] text-balance"
              style={{ fontWeight: 380 }}
            >
              {p.commands.h2a} <em className="text-[var(--terracotta)] italic">{p.commands.h2em}</em>
            </h2>
            <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] max-w-[560px] text-pretty">
              {p.commands.description}
            </p>

            {/* Action verbs — not just creating */}
            <div className="mt-8 flex flex-wrap items-center gap-x-2.5 gap-y-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                {p.commands.actionsLead}
              </span>
              {p.commands.actions.map((act) => (
                <span
                  key={act}
                  className="rounded-full border border-[var(--terracotta)]/30 bg-[var(--terracotta)]/[0.06] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--terracotta)]"
                >
                  {act}
                </span>
              ))}
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                {p.commands.actionsTail}
              </span>
            </div>
          </div>

          {/* Demo: two inputs → one confirmation chip */}
          <div className="w-full max-w-[520px] mx-auto lg:mx-0 lg:ml-auto">
            <div
              className="rounded-[18px] border p-5 md:p-6 flex flex-col gap-4"
              style={{
                background: "var(--paper-2)",
                borderColor: "var(--hair)",
                boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
              }}
            >
              <InputRow label={p.commands.naturalLabel} input={p.commands.naturalInput} />
              <InputRow label={p.commands.slashLabel} input={p.commands.slashInput} />

              <div className="flex items-center justify-center">
                <ArrowDown className="h-4 w-4 text-[var(--terracotta)]" aria-hidden />
              </div>

              <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-3.5 py-3">
                <div className="mb-2 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[var(--terracotta)]">
                  {p.commands.understood}
                </div>
                <ConfirmChip verb={p.commands.chipVerb} detail={p.commands.chipDetail} />
                <p className="mt-2.5 font-display italic text-[12px] leading-snug text-[var(--ink-soft)]/75">
                  {p.commands.confirmHint}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2 · Memory (goals + recent reflections) ─────────── */}
      <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-[100px] z-[2]">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-center">
          <div>
            <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {p.memory.kicker}
            </div>
            <h2
              className="font-display text-[clamp(30px,3.6vw,48px)] leading-[1.06] tracking-[-0.02em] text-balance"
              style={{ fontWeight: 380 }}
            >
              {p.memory.h2a} <em className="text-[var(--terracotta)] italic">{p.memory.h2em}</em>
            </h2>
            <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] max-w-[560px] text-pretty">
              {p.memory.description}
            </p>
          </div>

          {/* Context card — what the assistant always holds */}
          <div className="w-full max-w-[420px] mx-auto lg:mx-0 lg:ml-auto">
            <div
              className="rounded-[18px] border overflow-hidden"
              style={{
                background: "var(--paper)",
                borderColor: "var(--hair)",
                boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
              }}
            >
              <div className="flex items-center gap-2.5 border-b px-4 py-3" style={{ borderColor: "var(--hair)" }}>
                <span className="relative h-[18px] w-[18px] rounded-full border border-[var(--ink)] bg-[var(--paper)] after:absolute after:inset-[3px] after:rounded-full after:bg-[var(--terracotta)] after:content-['']" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                  {assistantName}
                </span>
              </div>
              <ul className="flex flex-col gap-2.5 p-4">
                {p.memory.pills.map((pill, i) => {
                  const Icon = pillIcons[i] ?? Target;
                  return (
                    <li
                      key={pill}
                      className="flex items-center gap-3 rounded-xl border px-3.5 py-3"
                      style={{ borderColor: "var(--hair)", background: "var(--paper-2)" }}
                    >
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-[var(--terracotta)]/12 text-[var(--terracotta)]">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="text-[14px] text-[var(--ink)]">{pill}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 3 · Strategic conversation ──────────────────────── */}
      <section className="relative max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-[100px] z-[2]">
        <div className="max-w-[720px] mb-10">
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {p.strategy.kicker}
          </div>
          <h2
            className="font-display text-[clamp(30px,3.6vw,48px)] leading-[1.06] tracking-[-0.02em] text-balance"
            style={{ fontWeight: 380 }}
          >
            {p.strategy.h2a} <em className="text-[var(--terracotta)] italic">{p.strategy.h2em}</em>
          </h2>
          <p className="mt-5 text-[16px] md:text-[18px] leading-[1.65] text-[var(--ink-soft)] text-pretty">
            {p.strategy.description}
          </p>
          <p className="mt-4 font-display italic text-[16px] md:text-[18px] leading-[1.55] text-[var(--ink-soft)] text-pretty">
            {en
              ? "This is AI-powered reflection — not advice from the outside, but questions that lead you back to your own goals."
              : "To je refleksija s pomočjo AI — ne nasveti od zunaj, ampak vprašanja, ki te vodijo nazaj k tvojim ciljem."}
          </p>
        </div>

        <div className="max-w-[840px] flex flex-col gap-8">
          {p.strategy.dialogue.map((turn) => (
            <div
              key={turn.user}
              className="rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/30 p-6 md:p-8 flex flex-col gap-5"
            >
              {/* User */}
              <div className="grid grid-cols-[24px_1fr] items-start gap-3">
                <span className="mt-1 h-[24px] w-[24px] shrink-0 rounded-full bg-[var(--terracotta)]" />
                <div className="min-w-0">
                  <div className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
                    {youLabel}
                  </div>
                  <p className="font-display italic text-[clamp(17px,1.6vw,21px)] leading-[1.45] text-[var(--ink)]">
                    {turn.user}
                  </p>
                </div>
              </div>
              {/* Assistant */}
              <div className="grid grid-cols-[24px_1fr] items-start gap-3">
                <span className="mt-1 h-[24px] w-[24px] shrink-0 rounded-full relative border border-[var(--ink)] bg-[var(--paper)] after:absolute after:inset-[4px] after:rounded-full after:bg-[var(--terracotta)] after:content-['']" />
                <div className="min-w-0">
                  <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[var(--terracotta)]">
                    {assistantName}
                  </div>
                  <p className="text-[15px] md:text-[16px] leading-[1.65] text-[var(--ink)] text-pretty">
                    {turn.ai}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
